import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { estimateMonthlySavingsUsd } from '../../../lib/roiEstimate';

const fmtUsd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

type RoiCalculatorSectionProps = {
  /** When true, the title block is omitted (e.g. standalone page supplies an h1 above). */
  hideIntro?: boolean;
};

export default function RoiCalculatorSection({ hideIntro = false }: RoiCalculatorSectionProps) {
  const { t } = useTranslation();
  const [ordersPerDay, setOrdersPerDay] = useState(500);
  const [riders, setRiders] = useState(40);
  const [delayCost, setDelayCost] = useState(4.5);

  const result = useMemo(
    () => estimateMonthlySavingsUsd(ordersPerDay, riders, delayCost),
    [ordersPerDay, riders, delayCost]
  );

  return (
    <section
      className="relative py-16 lg:py-24 bg-gradient-to-b from-white via-slate-50/80 to-white border-y border-slate-200/80"
      aria-labelledby={hideIntro ? undefined : 'roi-calculator-heading'}
      aria-label={hideIntro ? t('home.roiCalculator.title') : undefined}
      id="roi-calculator"
    >
      <div className="max-w-5xl mx-auto px-6">
        {!hideIntro && (
          <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">
              {t('home.roiCalculator.eyebrow')}
            </p>
            <h2
              id="roi-calculator-heading"
              className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-3"
            >
              {t('home.roiCalculator.title')}
            </h2>
            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{t('home.roiCalculator.subtitle')}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-3 space-y-5">
            <label className="block">
              <span className="text-sm font-semibold text-slate-800">{t('home.roiCalculator.ordersLabel')}</span>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">{t('home.roiCalculator.ordersHelp')}</p>
              <input
                type="number"
                min={0}
                step={1}
                value={ordersPerDay}
                onChange={(e) => setOrdersPerDay(Number(e.target.value))}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-shadow"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-slate-800">{t('home.roiCalculator.ridersLabel')}</span>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">{t('home.roiCalculator.ridersHelp')}</p>
              <input
                type="number"
                min={0}
                step={1}
                value={riders}
                onChange={(e) => setRiders(Number(e.target.value))}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-shadow"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-slate-800">{t('home.roiCalculator.delayCostLabel')}</span>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">{t('home.roiCalculator.delayCostHelp')}</p>
              <input
                type="number"
                min={0}
                step={0.25}
                value={delayCost}
                onChange={(e) => setDelayCost(Number(e.target.value))}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-shadow"
              />
            </label>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border-2 border-blue-600/20 bg-gradient-to-br from-blue-600 to-blue-700 p-6 lg:p-8 text-white shadow-xl shadow-blue-900/20">
              <p className="text-xs font-bold uppercase tracking-wider text-blue-100/90 mb-2">
                {t('home.roiCalculator.outputLabel')}
              </p>
              <p className="text-4xl lg:text-[2.75rem] font-extrabold tracking-tight tabular-nums mb-6">
                {fmtUsd(result.total)}
                <span className="text-lg font-semibold text-blue-100/80"> /mo</span>
              </p>
              <ul className="space-y-2.5 text-sm text-blue-50/95 border-t border-white/20 pt-5 mb-6">
                <li className="flex justify-between gap-4">
                  <span>{t('home.roiCalculator.breakdownDelays')}</span>
                  <span className="font-semibold tabular-nums">{fmtUsd(result.fromDelays)}</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>{t('home.roiCalculator.breakdownUtil')}</span>
                  <span className="font-semibold tabular-nums">{fmtUsd(result.fromUtilization)}</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>{t('home.roiCalculator.breakdownManual')}</span>
                  <span className="font-semibold tabular-nums">{fmtUsd(result.fromManual)}</span>
                </li>
              </ul>
              <p className="text-[11px] leading-relaxed text-blue-100/80 mb-6">{t('home.roiCalculator.disclaimer')}</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <a
                  href="https://meetings.hubspot.com/aditya145"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold bg-white text-blue-700 hover:bg-blue-50 transition-colors shadow-md"
                >
                  {t('home.roiCalculator.cta')}
                  <i className="ri-arrow-right-line" aria-hidden />
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-4 py-3 rounded-xl text-sm font-medium text-white border border-white/30 hover:bg-white/10 transition-colors"
                >
                  {t('home.roiCalculator.ctaSecondary')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
